import React, { useState, useEffect } from 'react';
import { authApi, setUnauthorizedHandler } from './services/api';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Sidebar from './components/Sidebar';
import UserList from './components/users/UserList';
import UserDetail from './components/users/UserDetail';
import PostList from './components/content/PostList';
import PostDetail from './components/content/PostDetail';
import MarketplaceList from './components/content/MarketplaceList';
import MarketplaceDetail from './components/content/MarketplaceDetail';
import CommentList from './components/content/CommentList';
import CommentDetail from './components/content/CommentDetail';
import CourseManagement from './components/courses/CourseManagement';
import ReportList from './components/reports/ReportList';
import AuditLogList from './components/logs/AuditLogList';
import AnnouncementList from './components/announcements/AnnouncementList';
import FeedbackList from './components/feedback/FeedbackList';
import Monitoring from './components/Monitoring';
import UserEngagement from './components/stats/UserEngagement'; // Issue #291
import Revenue from './components/stats/Revenue'; // Issue #313
import BackupRestore from './components/BackupRestore'; // Issue #338
import ScheduledTestData from './components/beta/ScheduledTestData'; // Issue #359
import UploadedTestData from './components/beta/UploadedTestData'; // Issue #359
import BetaRatings from './components/beta/BetaRatings'; // Issue #366
import WebsiteSettings from './components/WebsiteSettings';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('adminCurrentPage') || 'dashboard';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(() => {
    return localStorage.getItem('adminSelectedUserId') || null;
  });
  const [selectedMarketplaceObjectId, setSelectedMarketplaceObjectId] = useState(() => {
    return localStorage.getItem('adminSelectedMarketplaceObjectId') || null;
  });
  const [selectedPostId, setSelectedPostId] = useState(() => {
    return localStorage.getItem('adminSelectedPostId') || null;
  });
  // Issue #410: Comment detail navigation
  const [selectedCommentId, setSelectedCommentId] = useState(() => {
    return localStorage.getItem('adminSelectedCommentId') || null;
  });

  // Persist currentPage to localStorage
  useEffect(() => {
    localStorage.setItem('adminCurrentPage', currentPage);
  }, [currentPage]);

  // Persist selectedUserId to localStorage
  useEffect(() => {
    if (selectedUserId) {
      localStorage.setItem('adminSelectedUserId', selectedUserId);
    } else {
      localStorage.removeItem('adminSelectedUserId');
    }
  }, [selectedUserId]);

  // Persist selectedMarketplaceObjectId to localStorage
  useEffect(() => {
    if (selectedMarketplaceObjectId) {
      localStorage.setItem('adminSelectedMarketplaceObjectId', selectedMarketplaceObjectId);
    } else {
      localStorage.removeItem('adminSelectedMarketplaceObjectId');
    }
  }, [selectedMarketplaceObjectId]);

  // Persist selectedPostId to localStorage
  useEffect(() => {
    if (selectedPostId) {
      localStorage.setItem('adminSelectedPostId', selectedPostId);
    } else {
      localStorage.removeItem('adminSelectedPostId');
    }
  }, [selectedPostId]);

  // Issue #410: Persist selectedCommentId to localStorage
  useEffect(() => {
    if (selectedCommentId) {
      localStorage.setItem('adminSelectedCommentId', selectedCommentId);
    } else {
      localStorage.removeItem('adminSelectedCommentId');
    }
  }, [selectedCommentId]);

  // Issue #388: Register global 401 handler to auto-logout on session expiry.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setIsLoggedIn(false);
      setAdminUser(null);
      setSessionExpired(true);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await authApi.getMe();
        setAdminUser(response.data);
        setIsLoggedIn(true);
      } catch (error) {
        setIsLoggedIn(false);
        setAdminUser(null);
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, []);

  const handleLogin = (admin) => {
    setAdminUser(admin);
    setIsLoggedIn(true);
    setSessionExpired(false);
    setCurrentPage('dashboard');
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsLoggedIn(false);
    setAdminUser(null);
    setCurrentPage('dashboard');
  };

  // Navigate to user detail page
  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    setCurrentPage('userDetail');
  };

  // Navigate back from user detail to user list
  const handleBackToUserList = () => {
    setSelectedUserId(null);
    setCurrentPage('users');
  };

  // Navigate to marketplace object detail page
  const handleMarketplaceObjectSelect = (objectId) => {
    setSelectedMarketplaceObjectId(objectId);
    setCurrentPage('marketplaceDetail');
  };

  // Navigate back from marketplace detail to marketplace list
  const handleBackToMarketplaceList = () => {
    setSelectedMarketplaceObjectId(null);
    setCurrentPage('marketplace');
  };

  // Navigate to post detail page
  const handlePostSelect = (postId) => {
    setSelectedPostId(postId);
    setCurrentPage('postDetail');
  };

  // Navigate back from post detail to post list
  const handleBackToPostList = () => {
    setSelectedPostId(null);
    setCurrentPage('posts');
  };

  // Issue #410: Navigate to comment detail page (reachable for auto-deleted comments)
  const handleCommentSelect = (commentId) => {
    setSelectedCommentId(commentId);
    setCurrentPage('commentDetail');
  };

  const handleBackToCommentList = () => {
    setSelectedCommentId(null);
    setCurrentPage('comments');
  };

  const renderContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'users':
        return <UserList onUserSelect={handleUserSelect} />;
      case 'userDetail':
        return <UserDetail userId={selectedUserId} onBack={handleBackToUserList} />;
      case 'posts':
        return <PostList onPostSelect={handlePostSelect} />;
      case 'postDetail':
        return <PostDetail postId={selectedPostId} onBack={handleBackToPostList} />;
      case 'marketplace':
        return <MarketplaceList onObjectSelect={handleMarketplaceObjectSelect} />;
      case 'marketplaceDetail':
        return <MarketplaceDetail marketplaceObjectId={selectedMarketplaceObjectId} onBack={handleBackToMarketplaceList} />;
      case 'comments':
        return <CommentList onCommentSelect={handleCommentSelect} />;
      case 'commentDetail':
        return <CommentDetail commentId={selectedCommentId} onBack={handleBackToCommentList} />;
      case 'courses':
        return <CourseManagement />;
      case 'reports':
        return <ReportList />;
      case 'logs':
        return <AuditLogList />;
      case 'announcements':
        return <AnnouncementList />;
      case 'feedback':
        return <FeedbackList />;
      case 'monitoring':
        return <Monitoring />;
      case 'userEngagement':
        return <UserEngagement />;
      case 'revenue':
        return <Revenue />;
      case 'backups':
        return <BackupRestore />;
      case 'betaScheduled':
        return <ScheduledTestData />;
      case 'betaUploaded':
        return <UploadedTestData />;
      case 'betaRatings':
        return <BetaRatings />;
      case 'settings':
        return <WebsiteSettings />;
      default:
        return <Dashboard />;
    }
  };

  if (isCheckingSession) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Lade...</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} sessionExpired={sessionExpired} />;
  }

  return (
    <div className="admin-layout">
      <Sidebar
        currentPage={currentPage}
        onNavigate={setCurrentPage}
        onLogout={handleLogout}
        adminUser={adminUser}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`admin-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {renderContent()}
      </main>
    </div>
  );
}

export default App;
